/**
 * Forms and attestations, as the client sees them.
 *
 * A form is a set of questions with a legal attestation under it — the
 * annual conflict-of-interest declaration, the confidentiality agreement,
 * the "I have read the code of conduct" — assigned to everyone it targets
 * with a due date. A submission is an immutable record of what one person
 * answered and attested to, against the exact version of the form they
 * saw. Shapes mirror functions/src/formsAttestation.
 */

import { hasAtLeast, type Role } from '../../types/roles';

export const FORM_STATUSES = ['draft', 'published', 'retired'] as const;
export type FormStatus = (typeof FORM_STATUSES)[number];
export const STATUS_LABEL: Record<FormStatus, string> = { draft: 'Draft', published: 'Published', retired: 'Retired' };
export const STATUS_TONE: Record<FormStatus, 'neutral' | 'ok'> = { draft: 'neutral', published: 'ok', retired: 'neutral' };

export const ASSIGNMENT_STATUSES = ['pending', 'completed', 'overdue', 'waived'] as const;
export type AssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number];
export const ASSIGNMENT_LABEL: Record<AssignmentStatus, string> = { pending: 'To do', completed: 'Completed', overdue: 'Overdue', waived: 'Waived' };
export const ASSIGNMENT_TONE: Record<AssignmentStatus, 'neutral' | 'ok' | 'warn' | 'danger'> = { pending: 'warn', completed: 'ok', overdue: 'danger', waived: 'neutral' };

export const FIELD_TYPES = [
  'shortText', 'longText', 'number', 'yesNo', 'checkbox', 'radio', 'dropdown',
  'date', 'fileUpload', 'signature', 'acknowledgementCheckbox',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];
export const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  shortText: 'Short answer', longText: 'Long answer', number: 'Number', yesNo: 'Yes or no',
  checkbox: 'Checkbox', radio: 'Choose one', dropdown: 'Dropdown', date: 'Date',
  fileUpload: 'File', signature: 'Signature', acknowledgementCheckbox: 'Acknowledgement',
};
export const FIELD_TYPE_HINT: Record<FieldType, string> = {
  shortText: 'A line of text.', longText: 'A paragraph.', number: 'A number, with optional bounds.',
  yesNo: 'Two buttons.', checkbox: 'A box to tick.', radio: 'One of the options you list.',
  dropdown: 'One of the options, from a list.', date: 'A calendar date.', fileUpload: 'A document or image.',
  signature: 'The person types their full legal name.', acknowledgementCheckbox: 'A box that must be ticked to say "I have read and understood".',
};
export const CHOICE_TYPES: readonly FieldType[] = ['radio', 'dropdown'];

/** What kind of record the signed form is; who files it and what a surveyor asks for. */
export const FORM_CATEGORIES = [
  'policy_acknowledgement', 'confidentiality', 'conflict_of_interest', 'training_completion',
  'safety_attestation', 'licensure_certification', 'other',
] as const;
export type FormCategory = (typeof FORM_CATEGORIES)[number];
export const FORM_CATEGORY_LABEL: Record<FormCategory, string> = {
  policy_acknowledgement: 'Policy acknowledgement',
  confidentiality: 'Confidentiality agreement',
  conflict_of_interest: 'Conflict of interest disclosure',
  training_completion: 'Training completion',
  safety_attestation: 'Safety attestation',
  licensure_certification: 'Licensure or certification',
  other: 'Other',
};

export const AUDIENCES = ['all', 'departments', 'roles', 'users'] as const;
export type Audience = (typeof AUDIENCES)[number];
export const AUDIENCE_LABEL: Record<Audience, string> = { all: 'Everyone', departments: 'Departments', roles: 'Roles', users: 'Named people' };

function member<T extends readonly string[]>(list: T, v: unknown, fallback: T[number]): T[number] {
  return (list as readonly unknown[]).includes(v) ? (v as T[number]) : fallback;
}
export const toStatus = (v: unknown) => member(FORM_STATUSES, v, 'draft');
export const toAssignmentStatus = (v: unknown) => member(ASSIGNMENT_STATUSES, v, 'pending');
export const toFieldType = (v: unknown) => member(FIELD_TYPES, v, 'shortText');
export const toAudience = (v: unknown) => member(AUDIENCES, v, 'all');
export const toCategory = (v: unknown) => member(FORM_CATEGORIES, v, 'policy_acknowledgement');

export interface ValidationRules {
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  regex?: string;
}

export interface FieldDef {
  id: string;
  type: FieldType;
  label: string;
  description: string | null;
  helpText: string | null;
  required: boolean;
  options: string[];
  validationRules: ValidationRules | null;
}

export interface FormSummary {
  id: string;
  title: string;
  status: FormStatus;
  departmentIds: string[];
  currentVersionNumber: number | null;
  updatedAt: string | null;
}

/** The form document itself, as read directly: everything the builder set. */
export interface FormRecord extends FormSummary {
  description: string | null;
  instructions: string | null;
  fields: FieldDef[];
  targetAudience: Audience;
  targetDepartmentIds: string[];
  targetRoles: string[];
  targetUserIds: string[];
  requiresLegalAttestation: boolean;
  legalAttestationText: string;
  formCategory: FormCategory;
  /** Months between signings; null for a one-time form. */
  renewalIntervalMonths: number | null;
  currentVersionId: string | null;
  createdAt: string | null;
  publishedAt: string | null;
  retiredAt: string | null;
}

/** An immutable published version: what a person actually answered. */
export interface FormVersion {
  id: string;
  versionNumber: number;
  publishedAt: string | null;
  title: string;
  description: string | null;
  instructions: string | null;
  fields: FieldDef[];
  requiresLegalAttestation: boolean;
  legalAttestationText: string;
}

export interface Assignment {
  id: string;
  formId: string;
  formVersionId: string;
  formVersionNumber: number;
  formTitle: string;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  status: AssignmentStatus;
  dueDate: string | null;
  assignedAt: string | null;
  completedAt: string | null;
  waivedAt: string | null;
  waivedReason: string | null;
  submissionId: string | null;
}

export type AnswerValue = string | number | boolean;

export interface SubmissionResponse {
  fieldId: string;
  type: FieldType;
  labelSnapshot: string;
  requiredSnapshot: boolean;
  value: AnswerValue | null;
}

export interface Submission {
  id: string;
  assignmentId: string;
  formId: string;
  formVersionNumber: number;
  userId: string;
  userEmail: string | null;
  userDisplayName: string | null;
  submittedAt: string | null;
  responses: SubmissionResponse[];
  legalAttestationTextSnapshot: string;
}

/** The builder's draft of a field: strings for the numeric rules while typing. */
export interface FieldDraft {
  id: string;
  type: FieldType;
  label: string;
  description: string;
  helpText: string;
  required: boolean;
  options: string[];
  minValue: string;
  maxValue: string;
  minLength: string;
  maxLength: string;
}

export interface FormFields {
  title: string;
  description: string;
  instructions: string;
  fields: FieldDraft[];
  targetAudience: Audience;
  targetDepartmentIds: string[];
  targetRoles: string[];
  targetUserIds: string[];
  requiresLegalAttestation: boolean;
  legalAttestationText: string;
  formCategory: FormCategory;
  /** Digits or empty; empty means one-time. */
  renewalIntervalMonths: string;
}

export const DEFAULT_LEGAL_TEXT =
  'I attest that the information I have provided is true, complete and correct to the best of my knowledge, and I understand that it becomes part of my record.';

export const EMPTY_FORM: FormFields = {
  title: '', description: '', instructions: '', fields: [], targetAudience: 'all',
  targetDepartmentIds: [], targetRoles: [], targetUserIds: [], requiresLegalAttestation: true,
  legalAttestationText: DEFAULT_LEGAL_TEXT, formCategory: 'policy_acknowledgement', renewalIntervalMonths: '',
};

let seq = 0;
/** A field id the builder can keep stable across edits. */
export function newFieldId(): string {
  seq += 1;
  return `f-${Date.now().toString(36)}-${seq.toString(36)}`;
}

export function newField(type: FieldType = 'shortText'): FieldDraft {
  return { id: newFieldId(), type, label: '', description: '', helpText: '', required: true, options: [], minValue: '', maxValue: '', minLength: '', maxLength: '' };
}

export function toDraft(f: FieldDef): FieldDraft {
  const r = f.validationRules ?? {};
  const n = (v: number | undefined) => (v === undefined ? '' : String(v));
  return {
    id: f.id, type: f.type, label: f.label, description: f.description ?? '', helpText: f.helpText ?? '',
    required: f.required, options: f.options, minValue: n(r.minValue), maxValue: n(r.maxValue),
    minLength: n(r.minLength), maxLength: n(r.maxLength),
  };
}

/** A field as the create and update callables want it. Empty optionals are left out. */
export function toFieldDef(d: FieldDraft): Record<string, unknown> {
  const out: Record<string, unknown> = { id: d.id, type: d.type, label: d.label.trim(), required: d.required };
  if (d.description.trim()) out.description = d.description.trim();
  if (d.helpText.trim()) out.helpText = d.helpText.trim();
  if (CHOICE_TYPES.includes(d.type)) out.options = d.options;
  const rules: Record<string, number> = {};
  const num = (s: string) => (s.trim() === '' ? undefined : Number(s));
  if (d.type === 'number') {
    const lo = num(d.minValue), hi = num(d.maxValue);
    if (lo !== undefined && Number.isFinite(lo)) rules.minValue = lo;
    if (hi !== undefined && Number.isFinite(hi)) rules.maxValue = hi;
  }
  if (d.type === 'shortText' || d.type === 'longText') {
    const lo = num(d.minLength), hi = num(d.maxLength);
    if (lo !== undefined && Number.isInteger(lo) && lo >= 0) rules.minLength = lo;
    if (hi !== undefined && Number.isInteger(hi) && hi >= 1) rules.maxLength = hi;
  }
  if (Object.keys(rules).length) out.validationRules = rules;
  return out;
}

/** What is wrong with the builder's form, or null. In the order a person reads it. */
export function formProblem(f: FormFields): string | null {
  if (!f.title.trim()) return 'Give the form a title.';
  if (f.fields.length === 0) return 'Add at least one field.';
  for (const [i, d] of f.fields.entries()) {
    if (!d.label.trim()) return `Field ${i + 1} needs a label.`;
    if (CHOICE_TYPES.includes(d.type) && d.options.length === 0) return `"${d.label.trim()}" needs at least one option.`;
  }
  if (!f.legalAttestationText.trim()) return 'The attestation statement is required.';
  if (f.targetAudience === 'departments' && f.targetDepartmentIds.length === 0) return 'Choose at least one department.';
  if (f.targetAudience === 'roles' && f.targetRoles.length === 0) return 'Choose at least one role.';
  if (f.targetAudience === 'users' && f.targetUserIds.length === 0) return 'Choose at least one person.';
  return null;
}

/** Mirrors the callables' status and role checks. */
export function formActions(status: FormStatus, roles: readonly Role[]) {
  const author = hasAtLeast(roles, 'author');
  const admin = hasAtLeast(roles, 'admin');
  return {
    edit: author && status === 'draft',
    publish: admin && (status === 'draft' || status === 'published'),
    retire: admin && status !== 'retired',
  };
}

export function assignmentActions(a: Assignment, roles: readonly Role[], uid: string | null) {
  const open = a.status === 'pending' || a.status === 'overdue';
  return {
    complete: open && !!uid && a.userId === uid,
    waive: open && hasAtLeast(roles, 'admin'),
    viewSubmission: !!a.submissionId && (a.userId === uid || hasAtLeast(roles, 'admin')),
  };
}

/**
 * What a field's answer is missing or wrong, before it is sent — the same
 * checks the server makes (required, options, number bounds) plus the ones
 * the server leaves to the person (an acknowledgement must be ticked, a
 * signature must be a name). Null means fine.
 */
export function answerProblem(field: FieldDef, value: AnswerValue | null | undefined): string | null {
  // A box that must be ticked: untouched and unticked are the same thing,
  // and the message says what to do rather than that something is missing.
  if ((field.type === 'acknowledgementCheckbox' || field.type === 'checkbox') && field.required && value !== true) {
    return field.type === 'acknowledgementCheckbox' ? 'Tick to acknowledge.' : 'Tick the box.';
  }
  const empty = value === undefined || value === null || value === '';
  if (field.required && empty) return 'Required.';
  if (empty) return null;
  const r = field.validationRules ?? {};
  switch (field.type) {
    case 'acknowledgementCheckbox':
    case 'checkbox':
      return null;
    case 'radio':
    case 'dropdown':
      if (field.options.length && !field.options.includes(String(value))) return 'Choose one of the options.';
      return null;
    case 'number': {
      const n = Number(value);
      if (!Number.isFinite(n)) return 'Enter a number.';
      if (r.minValue !== undefined && n < r.minValue) return `At least ${r.minValue}.`;
      if (r.maxValue !== undefined && n > r.maxValue) return `At most ${r.maxValue}.`;
      return null;
    }
    case 'shortText':
    case 'longText':
    case 'signature': {
      const s = String(value).trim();
      if (field.type === 'signature' && s.split(/\s+/).length < 2) return 'Type your full name.';
      if (r.minLength !== undefined && s.length < r.minLength) return `At least ${r.minLength} characters.`;
      if (r.maxLength !== undefined && s.length > r.maxLength) return `At most ${r.maxLength} characters.`;
      if (r.regex) { try { if (!new RegExp(r.regex).test(s)) return 'Not in the expected form.'; } catch { /* a bad pattern is the form's fault, not the person's */ } }
      return null;
    }
    default:
      return null;
  }
}

/** A submitted value in words, for the receipt. */
export function answerText(type: FieldType, value: AnswerValue | null): string {
  if (value === null || value === undefined || value === '') return 'No answer';
  if (type === 'yesNo') return value === true ? 'Yes' : 'No';
  if (type === 'checkbox' || type === 'acknowledgementCheckbox') return value === true ? 'Yes' : 'No';
  return String(value);
}
